import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setSelectedTab } from '../../../redux/features/chatUi/chatUiSlice';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { selectSelectedChatUi } from '../../../domain/selectors/selected/chat/selectSelectedChatUi';
import { selectSelectedSessionId } from '../../../domain/selectors/base/chat/selectSelectedSessionId';
import { AssistantsChatSession, AssistantType } from '../../../domain/chat/models/ChatSession';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';

export type EngagementTab = 'inputs' | 'ea-chat';

export interface UseEngagementResult {
	/** The currently selected session id, or null if nothing is selected. */
	selectedSessionId: string | null;
	/** True when the selected session is an Engagement-type Assistants session. */
	hasEngagementSession: boolean;
	/**
	 * True when the session is the top-level engagement parent (the one that owns
	 * the Inputs tab and spawns child document-generation sessions).
	 */
	isParentSession: boolean;
	/**
	 * True when the session is a child document-generation session created by the
	 * Generate button. Child sessions only show the Chat tab.
	 */
	isChildSession: boolean;
	/** Which tab is currently active. */
	selectedTab: EngagementTab;
	/** Tabs that should be rendered in the tab bar for this session. */
	visibleTabs: EngagementTab[];
	onTabSelect: (tab: EngagementTab) => void;
}

const useEngagement = (): UseEngagementResult => {
	const dispatch = useAppDispatch();
	const selectedSessionId = useAppSelector(selectSelectedSessionId);
	const selectedSession = useAppSelector(selectSelectedSession);
	const selectedChatUi = useAppSelector(selectSelectedChatUi);

	// Fall back to 'inputs' for parent sessions; we'll correct this in the effect below.
	const selectedTab = (selectedChatUi.selectedTab as EngagementTab | undefined) ?? 'inputs';

	const isEngagementSession = useMemo((): boolean => {
		if (!selectedSession) return false;
		if (selectedSession.providerType !== ChatProviderType.Assistants) return false;
		const s = selectedSession as AssistantsChatSession;
		return s.assistantType === AssistantType.Engagement;
	}, [selectedSession]);

	const isParentSession = useMemo((): boolean => {
		if (!isEngagementSession || !selectedSession) return false;
		const s = selectedSession as AssistantsChatSession;
		// A parent session has no parentChatSessionId.
		return !s.parentChatSessionId;
	}, [isEngagementSession, selectedSession]);

	const isChildSession = useMemo((): boolean => isEngagementSession && !isParentSession, [
		isEngagementSession,
		isParentSession,
	]);

	const visibleTabs = useMemo((): EngagementTab[] => {
		if (!isEngagementSession) return ['inputs'];
		return isParentSession ? ['inputs', 'ea-chat'] : ['ea-chat'];
	}, [isEngagementSession, isParentSession]);

	// When the session changes, reset the active tab to a sensible default:
	// - parent sessions start on "inputs"
	// - child sessions start on "ea-chat"
	useEffect(() => {
		if (!selectedSessionId || !isEngagementSession) return;
		const defaultTab: EngagementTab = isParentSession ? 'inputs' : 'ea-chat';
		dispatch(setSelectedTab({ sessionId: selectedSessionId, selectedTab: defaultTab }));
	}, [selectedSessionId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only on session id change

	const onTabSelect = useCallback(
		(tab: EngagementTab): void => {
			if (!selectedSessionId) return;
			dispatch(setSelectedTab({ sessionId: selectedSessionId, selectedTab: tab }));
		},
		[dispatch, selectedSessionId],
	);

	return useMemo(
		() => ({
			selectedSessionId,
			hasEngagementSession: isEngagementSession,
			isParentSession,
			isChildSession,
			selectedTab,
			visibleTabs,
			onTabSelect,
		}),
		[
			selectedSessionId,
			isEngagementSession,
			isParentSession,
			isChildSession,
			selectedTab,
			visibleTabs,
			onTabSelect,
		],
	);
};

export default useEngagement;
